import { useCallback, useRef, useState } from 'react'
import {
  ActualWorkingHoursRow,
  ActualWorkingHoursSkelton,
  fetchActualWorkingHours,
  ActualWorkingHoursBatchInput,
  ActualWorkingHoursInput,
  convertInput2Date,
  convertBreakTime,
  updateBatch,
  ActualWorkingHoursRowScheduleBody,
  ActualWorkingHoursRowActualBody,
  calculateWorkTime,
  ActualWorkingHoursRowProjectBody,
  fetchTargetUsers,
  StandardWorkingInfo,
  ActualWorkingHoursSummaryRow,
  ActualWorkingHoursSummaryRowProjectBody,
  calculateBreakTime,
  RequestStatusRowProjectBody,
  RequestStatus,
  ActualWorkingHoursRequestBatchInput,
  ActualWorkingHoursRequestInput,
  updateRequestBatch,
  ActualWorkingHoursRatioRow,
  RequestRatioRowProjectBody,
} from '../ActualWorkingHours'
import { AgGridTreeHelper } from '../../../containers/BulkSheetView/lib/tree'
import _ from 'lodash'
import store from '../../../../store'
import {
  doNotRequireSave,
  requireSave,
} from '../../../../store/requiredSaveData'
import { APIResponse } from '../../../../lib/commons/api'
import { generateUuid } from '../../../../utils/uuids'
import Auth from '../../../../lib/commons/auth'
import DateVO from '../../../../vo/DateVO'
import TaskActualWorkAPI, {
  TaskActualWorkHour,
} from '../../../../lib/functions/taskActualWork'
import { UserBasic } from '../../../../domain/value-object/UserBasicVO'
import { featureFlagService } from '../../../../utils/feature-flag'
import { calculateRatio } from '../gridOptions'
import { convertDecimalHourToSeconds } from '../../../../utils/time'

export const useActualWorkingHoursData = () => {
  const [summaryRow, setSummaryRow] = useState<ActualWorkingHoursSummaryRow>(
    new ActualWorkingHoursSummaryRow()
  )
  const [ratioRow, setRatioRow] = useState<ActualWorkingHoursRatioRow>(
    new ActualWorkingHoursRatioRow()
  )
  const [data, setData] = useState<ActualWorkingHoursRow[]>([])
  const originalData = useRef<
    Map<
      string,
      Pick<
        ActualWorkingHoursRowActualBody,
        'workingHoursType' | 'startTime' | 'endTime' | 'breakTime' | 'remarks'
      >
    >
  >(new Map())

  const clearData = useCallback(() => {
    setData([])
    originalData.current.clear()
    setSummaryRow(new ActualWorkingHoursSummaryRow())
    setRatioRow(new ActualWorkingHoursRatioRow())
  }, [])

  const fetchRecords = useCallback(
    async (
      userUuid: string,
      projectUuid: string,
      startDate: string,
      endDate: string
    ) => {
      setData([])
      const response = await fetchActualWorkingHours({
        projectUuid,
        userUuid,
        startDate,
        endDate,
      })
      const sources: ActualWorkingHoursSkelton[] = response.json

      const responseTaskActualHour =
        await TaskActualWorkAPI.getTaskActualWorkHours({
          projectUuid,
          userUuid,
          startDate,
          endDate,
        })
      const taskActualWork: TaskActualWorkHour[] = responseTaskActualHour.json
      const rows = sources
        .map(v =>
          AgGridTreeHelper.convert(
            v,
            (s: ActualWorkingHoursSkelton): ActualWorkingHoursRow => {
              const targetTaskActualWorks = taskActualWork.filter(
                actualWork => actualWork.actualDate === s.workDate
              )

              let totalHour = 0
              if (projectUuid) {
                const target = targetTaskActualWorks.find(
                  v => v.projectUuid === projectUuid
                )
                totalHour = target?.hour
                  ? convertDecimalHourToSeconds(target.hour)
                  : 0
              } else {
                totalHour = targetTaskActualWorks.reduce(
                  (acc, cur) => acc + convertDecimalHourToSeconds(cur.hour),
                  0
                )
              }
              const row: ActualWorkingHoursRow = {
                type: 'WORK_HOUR',
                uuid: s.uuid,
                date: s.workDate,
                schedule: s.schedule
                  ? new ActualWorkingHoursRowScheduleBody(s.schedule)
                  : undefined,
                actual: s.actual
                  ? new ActualWorkingHoursRowActualBody(s.actual)
                  : undefined,
                totalActualHour: totalHour,
                projectHours: targetTaskActualWorks.map(actualWork => {
                  const body = new ActualWorkingHoursRowProjectBody()
                  body.projectUuid = actualWork.projectUuid
                  body.actualHour = convertDecimalHourToSeconds(actualWork.hour)
                  return body
                }),
                requestCheck: requestStatusCheck(targetTaskActualWorks),
                projectRequestStatus: targetTaskActualWorks.map(
                  actualWorkStatus => {
                    const body = new RequestStatusRowProjectBody()
                    body.projectUuid = actualWorkStatus.projectUuid
                    body.requestStatus = actualWorkStatus.requestStatus
                    body.requestUuid = actualWorkStatus.requestUuid ?? undefined
                    body.proxyUser = actualWorkStatus.proxyUser
                    return body
                  }
                ),
              } as ActualWorkingHoursRow
              row.workingHoursType =
                row.actual?.workingHoursType ??
                row.schedule?.workingHoursType ??
                ''
              row.remarks = row.actual?.remarks ?? row.schedule?.dateName ?? ''
              return row
            }
          )
        )
        .flat()
      setData(rows)
      originalData.current.clear()

      let sumBreak = 0
      let sumWorked = 0
      rows
        .filter(row => !!row.actual)
        .forEach(row => {
          originalData.current.set(
            row.uuid,
            _.cloneDeep({
              workingHoursType: row.actual!.workingHoursType,
              startTime: row.actual!.startTime,
              endTime: row.actual!.endTime,
              breakTime: row.actual!.breakTime,
              remarks: row.actual!.remarks,
            })
          )
          sumBreak += calculateBreakTime(row)
          sumWorked += calculateWorkTime(row)
        })

      const projectHourMap = new Map<
        string,
        ActualWorkingHoursSummaryRowProjectBody
      >()
      rows.forEach(row => {
        row.projectHours?.forEach(projectHour => {
          const body = projectHourMap.has(projectHour.projectUuid)
            ? (projectHourMap.get(
                projectHour.projectUuid
              ) as ActualWorkingHoursSummaryRowProjectBody)
            : new ActualWorkingHoursSummaryRowProjectBody(
                projectHour.projectUuid
              )
          body.actualHour += projectHour.actualHour
          projectHourMap.set(projectHour.projectUuid, body)
        })
        return projectHourMap
      })

      const projectRequestMap = new Map<string, RequestRatioRowProjectBody>()

      rows.forEach(row => {
        row.projectRequestStatus?.forEach(projectStatus => {
          const body = projectRequestMap.has(projectStatus.projectUuid)
            ? (projectRequestMap.get(
                projectStatus.projectUuid
              ) as RequestRatioRowProjectBody)
            : new RequestRatioRowProjectBody(projectStatus.projectUuid)
          if (projectStatus.requestStatus !== undefined) {
            body.requestStatusConunt = (body.requestStatusConunt ?? 0) + 1
          }
          if (projectStatus.requestStatus === RequestStatus.APPROVED) {
            body.approvedStatusConunt = (body.approvedStatusConunt ?? 0) + 1
          }
          projectRequestMap.set(projectStatus.projectUuid, body)
        })
      })
      const sumRow = new ActualWorkingHoursSummaryRow()
      sumRow.breakTime = sumBreak
      sumRow.workHour = sumWorked
      sumRow.totalActualHour = rows.reduce((sum, row) => {
        return sum + (row.totalActualHour || 0)
      }, 0)
      sumRow.projectHours = Array.from(projectHourMap.values())
      setSummaryRow(sumRow)
      if (featureFlagService.isEnabled('task-actual-work-application')) {
        const ratioRow = new ActualWorkingHoursRatioRow()
        const totalHour = rows.reduce((sum, row) => {
          return sum + (row.totalActualHour || 0)
        }, 0)
        ratioRow.totalActualHour = totalHour
        ratioRow.totalActualHourRatio =
          sumWorked > 0 ? calculateRatio(totalHour, sumWorked / 3600) : 0
        ratioRow.projectHours = Array.from(projectHourMap.values())
        ratioRow.projectRequestStatus = Array.from(projectRequestMap.values())
        setRatioRow(ratioRow)
      }
    },
    []
  )

  const requestStatusCheck = useCallback(
    (row: TaskActualWorkHour[]): boolean => {
      if (row.length === 0) return false
      const statusList = row.map(
        actualWorkStatus => actualWorkStatus.requestStatus
      )
      return statusList.every(
        status =>
          status === RequestStatus.APPROVED ||
          status === RequestStatus.REQUESTED
      )
    },
    []
  )

  const refresh = useCallback(
    async (
      userUuid: string,
      projectUuid: string,
      startDate: string,
      endDate: string
    ) => {
      const formatStart = new DateVO(startDate).formatForApi()!
      const formatEnd = new DateVO(endDate).formatForApi()!
      await fetchRecords(userUuid, projectUuid, formatStart, formatEnd)
      store.dispatch(doNotRequireSave())
    },
    [fetchRecords]
  )

  const updateWorkingHours = useCallback(
    async (rows: ActualWorkingHoursRow[]): Promise<APIResponse> => {
      const tenant = Auth.getCurrentTenant()
      const userUuid = tenant!.user!.uuid
      const batchInput: ActualWorkingHoursBatchInput = {
        added: convertRow2Request(
          rows.filter(row => !row.actual?.actualUuid),
          userUuid
        ),
        edited: convertRow2Request(
          rows.filter(row => !!row.actual?.actualUuid),
          userUuid
        ),
        deleted: [],
      }
      return updateBatch(batchInput)
    },
    []
  )

  const updateWorkingRequest = useCallback(
    async (rows: ActualWorkingHoursRow[]): Promise<APIResponse> => {
      const tenant = Auth.getCurrentTenant()
      const userUuid = tenant!.user!.uuid
      const batchInput: ActualWorkingHoursRequestBatchInput = {
        application: convertRow3Request(
          rows
            .map(row => ({
              ...row,
              projectRequestStatus: row.projectRequestStatus?.filter(
                status =>
                  status.requestUuid === undefined &&
                  status.requestStatus === undefined
              ),
            }))
            .filter(
              row =>
                row.projectRequestStatus && row.projectRequestStatus.length > 0
            ),
          userUuid
        ),
        reapplication: convertRow3Request(
          rows
            .map(row => ({
              ...row,
              projectRequestStatus: row.projectRequestStatus?.filter(
                status =>
                  status.requestUuid !== undefined &&
                  status.requestStatus === RequestStatus.SENT_BACK
              ),
            }))
            .filter(
              row =>
                row.projectRequestStatus && row.projectRequestStatus.length > 0
            ),
          userUuid
        ),
        cancel: convertRow3Request(
          rows
            .map(row => ({
              ...row,
              projectRequestStatus: row.projectRequestStatus?.filter(
                status =>
                  !row.requestCheck &&
                  status.requestStatus === RequestStatus.REQUESTED
              ),
            }))
            .filter(
              row =>
                row.projectRequestStatus && row.projectRequestStatus.length > 0
            ),
          userUuid
        ),
      }
      return updateRequestBatch(batchInput)
    },
    []
  )

  const save = useCallback(async (): Promise<APIResponse> => {
    const edited = data.filter(row => row.edited)
    const response = await updateWorkingHours(edited)
    return response
  }, [data, updateWorkingHours])

  const saveRequest = useCallback(async (): Promise<APIResponse> => {
    if (!featureFlagService.isEnabled('task-actual-work-application')) {
      return {
        status: 200,
        json: {},
        hasError: false,
        hasWarning: false,
      }
    }
    const edited = data.filter(row => row.edited)
    const response = await updateWorkingRequest(edited)
    return response
  }, [data, updateWorkingRequest])

  const convertRow2Request = (
    items: ActualWorkingHoursRow[],
    userUuid: string
  ): ActualWorkingHoursInput[] => {
    return items.map(row => {
      return {
        uuid: row.actual?.actualUuid ?? generateUuid(),
        userUuid,
        workingHoursType: row.workingHoursType ?? '',
        workDate: row.date ?? '',
        workStartAt: convertInput2Date(row.date, row.actual?.startTime),
        workEndAt: convertInput2Date(row.date, row.actual?.endTime),
        breakTime: convertBreakTime(row),
        remarks: row.remarks ?? '',
      }
    })
  }

  const convertRow3Request = (
    items: ActualWorkingHoursRow[],
    userUuid: string
  ): ActualWorkingHoursRequestInput[] => {
    return items.map(row => {
      return {
        userUuid,
        workDate: row.date ?? '',
        requestCheck: row.requestCheck,
        projectRequests: projectRequests(row),
      }
    })
  }

  const projectRequests = (
    row: ActualWorkingHoursRow
  ): RequestStatusRowProjectBody[] => {
    if (!row.projectRequestStatus) return [] as RequestStatusRowProjectBody[]

    return row.projectRequestStatus
      .filter(status => {
        if (
          row.requestCheck &&
          (status.requestStatus === RequestStatus.APPROVED ||
            status.requestStatus === RequestStatus.REQUESTED)
        ) {
          return false
        }
        if (
          !row.requestCheck &&
          (status.requestStatus === RequestStatus.SENT_BACK ||
            status.requestStatus === undefined ||
            status.requestStatus === RequestStatus.APPROVED)
        ) {
          return false
        }
        return true
      })
      .map(status => {
        const body = new RequestStatusRowProjectBody()
        body.projectUuid = status.projectUuid
        body.requestUuid = status.requestUuid
        return body
      })
  }

  const fetchUsers = useCallback(
    async (startDate: string, endDate: string): Promise<UserBasic[]> => {
      const formatStart = new DateVO(startDate).formatForApi()!
      const formatEnd = new DateVO(endDate).formatForApi()!
      const response = await fetchTargetUsers({
        startDate: formatStart,
        endDate: formatEnd,
      })
      const src = response.json
      const users: UserBasic[] = src.children.map(v => {
        return {
          uuid: v.user.uuid,
          name: v.user.name,
        } as UserBasic
      })
      return users
    },
    []
  )

  const getStandardWorkingInfo = useCallback(() => {
    const scheduleHour = data.map(value => {
      return value.schedule?.workHour ?? 0
    })
    const workDays = scheduleHour.reduce((prev, cur) => {
      return prev + (cur > 0 ? 1 : 0)
    }, 0)
    const rawStdHour = scheduleHour.reduce((prev, cur) => {
      return prev + cur
    }, 0)
    let sumTime = 0
    let sumDays = 0
    data.forEach(row => {
      sumTime += calculateWorkTime(row)
      if (!!row.actual && !!row.actual.startTime && !!row.actual.endTime) {
        sumDays++
      }
    })

    const diffHours = sumTime - rawStdHour * 3600
    const diffDays = sumDays - workDays
    const diffHoursPercent =
      rawStdHour > 0
        ? Math.round((diffHours / (rawStdHour * 3600)) * 10000) / 100
        : 0
    const diffDaysPercent =
      workDays > 0 ? Math.round((diffDays / workDays) * 10000) / 100 : 0

    return {
      standardWorkDays: workDays,
      standardWorkHours: rawStdHour,
      totalWorkHours: sumTime,
      totalWorkDays: sumDays,
      diffHours: diffHours,
      diffDays: diffDays,
      diffHoursPercent: diffHoursPercent,
      diffDaysPercent: diffDaysPercent,
    } as StandardWorkingInfo
  }, [data])

  const updateRequestCheck = useCallback(
    (targetRows: ActualWorkingHoursRow[], newValue: boolean) => {
      let hasChange = false
      setData(prevData =>
        prevData.map(row => {
          const targetRow = targetRows.find(target => target.uuid === row.uuid)
          if (!targetRow) return row
          if (
            targetRow.projectRequestStatus?.every(
              status => status.requestStatus === RequestStatus.APPROVED
            )
          ) {
            return row
          }
          if (row.requestCheck === newValue) return row
          hasChange = true
          return {
            ...row,
            requestCheck: newValue,
            edited: true,
          }
        })
      )
      hasChange && store.dispatch(requireSave())
    },
    []
  )

  return {
    data,
    summaryRow,
    ratioRow,
    refresh,
    save,
    saveRequest,
    updateRequestCheck,
    updateWorkingRequest,
    fetchUsers,
    clearData,
    getStandardWorkingInfo,
  }
}
